import { create } from "zustand";
import {
  deleteData,
  getData,
  getDataFile,
  postData,
  postDataFileDat,
  putData,
} from "../../../../lib/utils/formHelpers";
import { errorAlert, successAlert } from "../../../utils/alerts";
import { useSample as useSampleAlias } from "./useSample";
import { AnalysisType } from "./constants";
import {
  IsotopeResultCalculationPOSTTypes,
  IsotopeResultCalculationResponseTypes,
  KeysTabsAnalysis,
  PreparationTypes,
  ProccesingTypes,
  ResultTypes,
  ResultsTypes,
} from "../../../types/samples.types";
import {
  parseIsotopeData,
  removeCalculationByIndex,
  removeCalculationByIndexWithMeasurement,
  removeCountingByIndex,
  removeCountingByIndexWithMeasurement,
  removeMeasurementByIndex,
  removePreparationByIndex,
  updatePreparations,
} from "./helpers";
import { AnalysisWhitMeasurement } from "../config/phases/AnalysisPhases";
import { useEfficiencies } from "@services/servicesGenericsTables";
import { parseRptFileExe } from "../components/analysisComponents/IG/fileUploadAreaExe";
import { toExponentialUppercase } from "@app/utils/functions";

type UseAnalysisType = {
  loading: boolean;
  loadingDateAxe: boolean;
  loadingIsotopeResultCalculation: boolean;
  loadingResults: boolean;
  loadingFile: boolean;
  processingId?: number;
  preparationsList: PreparationTypes[];
  currentPreparation: {
    id: number | undefined;
    index: number | undefined;
    canEdit?: boolean;
  };
  currentCounting: {
    id: number | undefined;
    index: number | undefined;
    canEdit?: boolean;
  };
  currentCalculation: {
    id: number | undefined;
    index: number | undefined;
    result: any | undefined;
  };
  currentMeasurement: {
    id: number | undefined;
    index: number | undefined;
    canEdit?: boolean;
  };
  currentIsotopeResultCalculationResponseTypes: IsotopeResultCalculationResponseTypes[];
  setPreparationsList: (preparations: any[]) => void;
  setCurrentPreparation: (
    id?: number,
    index?: number,
    canEdit?: boolean
  ) => void;
  setCurrentCounting: (id?: number, index?: number, canEdit?: boolean) => void;
  setCurrentCalculation: (id?: number, index?: number, result?: object) => void;
  setCurrentMeasurement: (
    id?: number,
    index?: number,
    canEdit?: boolean
  ) => void;
  fetchPreparations: (preparations: PreparationTypes[]) => Promise<void>;
  saveProcessing: (
    processing: ProccesingTypes,
    type: string
  ) => Promise<number | undefined>;
  saveProcessingWhitMeasure: (
    processing: ProccesingTypes,
    type: string
  ) => Promise<number | undefined>;
  saveResultCalculate: (
    codeCurrentAnalysis: string,
    result: ResultsTypes
  ) => Promise<boolean>;
  getCurrentIds: () => {
    preparationId: number | undefined;
    measurementId: number | undefined;
    countingId: number | undefined;
    calculationId: number | undefined;
  };
  saveCalculate: (codeCurrentAnalysis: string) => Promise<ResultTypes>;
  resetState: () => void;
  resetInternalState: () => void;
  setProcessingId: (id: number) => void;
  remove: (
    idTypeAnalysis: number,
    type: string,
    id?: number,
    idxPreparation?: number,
    idxCounting?: number,
    idxCalculation?: number
  ) => Promise<void>;
  removeWithMeasure: (
    idTypeAnalysis: number,
    type: string,
    id?: number,
    idxPreparation?: number,
    idxMeasurement?: number,
    idxCounting?: number,
    idxCalculation?: number
  ) => Promise<void>;
  putResult: (id: number, toReport: boolean) => Promise<boolean>;
  fetchCalculation: (id: number) => Promise<ResultTypes>;
  saveFileITIG: (file: File, name: string) => Promise<boolean>;
  updateCoutingITIG: (processing: ProccesingTypes) => Promise<void>;
  importFileFromRoute: (
    route: string,
    action: "DOWNLOAD" | "TO_FORM"
  ) => Promise<string[] | undefined>;
  importFileCalculation: (
    nameFile: string,
    action: "DOWNLOAD" | "TO_FORM"
  ) => Promise<string | undefined>;
  calculateDataOutIGAxe: (
    routeFile1: string,
    routeFile2: string,
    equipment: any,
    date: string,
    calculation_self_absorption_factor: number,
    performance: number,
    calculation_geometric_factor: number,
    sample_amount: string,
    diffDate: number,
    cod_instalacion: string,
    ref_cliente: string
  ) => Promise<any>;
  calculateDataOutGT: (
    equipment: string,
    cpsSpectrumArea: string,
    absorptionFactor: string,
    geometricFactor: string,
    performance: string,
    amountSample: string,
    integralSpectrum: string,
    countingTime: string
  ) => Promise<any>;
  updateCalculationById: () => Promise<boolean>;
  saveIsotopeResultCalculation: (
    payload: IsotopeResultCalculationPOSTTypes[]
  ) => Promise<boolean>;
  fetchIsotopeResultCalculationByID: () => Promise<
    IsotopeResultCalculationResponseTypes[]
  >;
  deleteIsotopeResultCalculationByID: (id: number) => Promise<void>;
  updateRequestedByClient: (id: number, value: number) => Promise<void>;
  saveIsotopeResultAxe: (payload: any[]) => Promise<boolean>;
  fetchIsotopeResultAxeByID: () => Promise<any[]>;
  filterCurrentPreparation: () => PreparationTypes[];
};

export const useAnalysis = create<UseAnalysisType>((set, get) => ({
  loading: false,
  currentIsotopeResultCalculationResponseTypes: [],
  loadingDateAxe: false,
  loadingIsotopeResultCalculation: false,
  loadingFile: false,
  loadingResults: false,
  processingId: undefined,
  preparationsList: [],
  currentPreparation: { id: undefined, index: undefined },
  currentCounting: { id: undefined, index: undefined },
  currentCalculation: { id: undefined, index: undefined, result: undefined },
  currentMeasurement: { id: undefined, index: undefined },
  filterCurrentPreparation: () => {
    const { preparationsList, currentPreparation } = get();
    const filteredPreparations = preparationsList.find(
      (preparation) => preparation.id === currentPreparation.id
    );
    const currentAnalysisType = filteredPreparations?.analysis_type[0].codigo;
    const filteredPreparationsList = preparationsList.filter(
      (preparation) =>
        preparation.analysis_type[0].codigo === currentAnalysisType
    );

    return filteredPreparationsList;
  },
  setProcessingId: (id) => {
    const sample = useSampleAlias.getState().get(id);
    const convertSampleProcessing = sample?.processings;

    const idProcessing =
      convertSampleProcessing && convertSampleProcessing.length > 0
        ? Math.max(...convertSampleProcessing.map((p: any) => p.id))
        : undefined;

    set({ processingId: idProcessing });
  },
  resetState: () =>
    set({
      processingId: undefined,
      preparationsList: [],
      currentPreparation: { id: undefined, index: undefined },
      currentCounting: { id: undefined, index: undefined },
      currentMeasurement: { id: undefined, index: undefined },
      currentCalculation: {
        id: undefined,
        index: undefined,
        result: undefined,
      },
    }),
  resetInternalState: () =>
    set({
      currentPreparation: { id: undefined, index: undefined },
      currentCounting: { id: undefined, index: undefined },
      currentMeasurement: { id: undefined, index: undefined },
      currentCalculation: {
        id: undefined,
        index: undefined,
        result: undefined,
      },
    }),
  getCurrentIds: () => {
    const {
      currentPreparation,
      currentCounting,
      currentCalculation,
      currentMeasurement,
    } = get();

    return {
      preparationId: currentPreparation.id,
      measurementId: currentMeasurement.id,
      countingId: currentCounting.id,
      calculationId: currentCalculation.id,
      processingId: get().processingId,
    };
  },
  setPreparationsList: (preparations) =>
    set({ preparationsList: preparations }),
  setCurrentPreparation: (id, index, canEdit) =>
    set({ currentPreparation: { id, index, canEdit } }),
  setCurrentCounting: (id, index, canEdit) => {
    set({ currentCounting: { id, index, canEdit } });
  },
  setCurrentCalculation: (id, index, result) =>
    set({ currentCalculation: { id, index, result } }),
  setCurrentMeasurement: (id, index, canEdit) =>
    set({ currentMeasurement: { id, index, canEdit } }),
  fetchPreparations: async (preparations) => {
    set({ loading: true });
    try {
      const endpointPreparations = `/preparation/`;

      const preparationsData = await Promise.all(
        preparations.map(async (preparation: { id: number }) => {
          try {
            const { data: responsePreparations } = await getData(
              `${endpointPreparations}${preparation.id}`
            );

            if (responsePreparations && responsePreparations.state) {
              const preparationData = responsePreparations;

              if (
                AnalysisWhitMeasurement.includes(
                  responsePreparations.analysis_type[0].codigo
                )
              ) {
                if (
                  preparationData.aa_measures &&
                  preparationData.aa_measures.length > 0
                ) {
                  const measurementsDetails = await Promise.all(
                    preparationData.aa_measures.map(
                      async (aa_measure: { id: number }) => {
                        try {
                          const { data: responseMeasurement } = await getData(
                            `/aa-measure/${aa_measure.id}`
                          );

                          if (
                            responseMeasurement.countings &&
                            responseMeasurement.countings.length > 0
                          ) {
                            const countingsDetails = await Promise.all(
                              responseMeasurement.countings.map(
                                async (counting: { id: number }) => {
                                  try {
                                    const { data: responseCounting } =
                                      await getData(`/counting/${counting.id}`);

                                    if (
                                      responseCounting &&
                                      responseCounting.calculations &&
                                      responseCounting.calculations.length > 0
                                    ) {
                                      const calculationsDetails =
                                        await Promise.all(
                                          responseCounting.calculations.map(
                                            async (calculation: {
                                              id: number;
                                            }) => {
                                              try {
                                                const {
                                                  data: responseCalculation,
                                                } = await getData(
                                                  `/calculation/${calculation.id}`
                                                );
                                                return responseCalculation.state
                                                  ? responseCalculation
                                                  : undefined;
                                              } catch (error) {
                                                console.error(
                                                  `Error fetching calculation with ID ${calculation.id}:`,
                                                  error
                                                );
                                                return null;
                                              }
                                            }
                                          )
                                        );

                                      responseCounting.calculations =
                                        calculationsDetails.filter(Boolean);
                                    }
                                    return responseCounting;
                                  } catch (error) {
                                    console.error(
                                      `Error fetching counting with ID ${counting.id}:`,
                                      error
                                    );
                                    return null;
                                  }
                                }
                              )
                            );

                            responseMeasurement.countings =
                              countingsDetails.filter(Boolean);
                          }
                          return responseMeasurement;
                        } catch (error) {
                          console.error(
                            `Error fetching aa_measure with ID ${aa_measure.id}:`,
                            error
                          );
                          return null;
                        }
                      }
                    )
                  );

                  preparationData.aa_measures =
                    measurementsDetails.filter(Boolean);
                }

                return preparationData;
              } else {
                if (
                  preparationData.countings &&
                  preparationData.countings.length > 0
                ) {
                  const countingsDetails = await Promise.all(
                    preparationData.countings.map(
                      async (counting: { id: number }) => {
                        try {
                          const { data: responseCounting } = await getData(
                            `/counting/${counting.id}`
                          );
                          if (responseCounting.state) {
                            if (
                              responseCounting &&
                              responseCounting.calculations &&
                              responseCounting.calculations.length > 0
                            ) {
                              const calculationsDetails = await Promise.all(
                                responseCounting.calculations.map(
                                  async (calculation: { id: number }) => {
                                    try {
                                      const { data: responseCalculation } =
                                        await getData(
                                          `/calculation/${calculation.id}`
                                        );
                                      return responseCalculation.state
                                        ? responseCalculation
                                        : undefined;
                                    } catch (error) {
                                      console.error(
                                        `Error fetching calculation with ID ${calculation.id}:`,
                                        error
                                      );
                                      return null;
                                    }
                                  }
                                )
                              );

                              responseCounting.calculations =
                                calculationsDetails.filter(Boolean);
                            }
                          } else {
                            return null;
                          }
                          return responseCounting;
                        } catch (error) {
                          console.error(
                            `Error fetching counting with ID ${counting.id}:`,
                            error
                          );
                          return null;
                        }
                      }
                    )
                  );

                  preparationData.countings = countingsDetails.filter(Boolean);
                }

                return preparationData;
              }
            } else {
              console.error(
                `Error fetching data for preparation ID ${preparation.id}`
              );
              return null;
            }
          } catch (error) {
            console.error(
              `Error fetching preparation with ID ${preparation.id}:`,
              error
            );
            return null;
          }
        })
      );

      const filteredPreparations = preparationsData.filter(Boolean);
      set({ preparationsList: filteredPreparations });
    } catch (error) {
      console.error("Error fetching processing data:", error);
    } finally {
      set({ loading: false });
    }
  },
  saveProcessing: async (processing: ProccesingTypes, type: string) => {
    try {
      const isNew = !processing?.id;
      const endpoint = `/${type}/${isNew ? "create" : processing.id + "/edit"}`;

      const response = isNew
        ? await postData(endpoint, processing)
        : await putData(endpoint, processing);

      if (response.success) {
        if (type === AnalysisType.PREPARATION) {
          const currentPreparations = get().preparationsList;
          const currentIndex = get().currentPreparation.index;

          if (currentIndex !== undefined && currentIndex >= 0) {
            const filteredPreparations = currentPreparations.filter(
              (preparation) =>
                preparation.analysis_type.some(
                  (analysis) => analysis.id === processing.analysis_type
                )
            );

            const updatedFilteredPreparations = filteredPreparations.map(
              (preparation, index) => {
                if (index === currentIndex) {
                  return {
                    ...preparation,
                    ...response.data,
                  };
                }
                return preparation;
              }
            );

            const updatedPreparations = currentPreparations.map(
              (preparation) => {
                if (
                  preparation.analysis_type.some(
                    (analysis) => analysis.id === processing.analysis_type
                  )
                ) {
                  return updatedFilteredPreparations.shift()!;
                }
                return preparation;
              }
            );

            set({
              preparationsList: updatedPreparations.sort((a, b) => a.id - b.id),
            });
          }

          set({
            currentPreparation: {
              ...get().currentPreparation,
              id: response.data.id,
            },
          });
        }

        if (type === AnalysisType.COUNTING) {
          const currentPreparations = get().preparationsList;
          const preparationIndex = get().currentPreparation.index;
          const currentCountingIndex = get().currentCounting.index;
          const currentPreparationId = get().currentPreparation.id;

          if (
            preparationIndex !== undefined &&
            preparationIndex >= 0 &&
            currentCountingIndex !== undefined &&
            currentCountingIndex >= 0
          ) {
            const updatedFilteredPreparations = currentPreparations.map(
              (preparation, index) => {
                if (
                  preparation.analysis_type.some(
                    (analysis) =>
                      analysis.id === processing.analysis_type &&
                      preparation.id === currentPreparationId
                  )
                ) {
                  const updatedCountings = [
                    ...(preparation.countings as any[]),
                  ];

                  updatedCountings[currentCountingIndex] = response.data;
                  return {
                    ...preparation,
                    countings: updatedCountings,
                  };
                }
                return preparation;
              }
            );

            set({
              preparationsList: updatedFilteredPreparations,
            });
          }

          set({
            currentCounting: {
              ...get().currentCounting,
              id: response.data.id,
            },
          });
        }

        if (type === AnalysisType.CALCULATION) {
          const currentPreparations = get().preparationsList;
          const currentPreparationId = get().currentPreparation.id;
          const currentCountingIndex = get().currentCounting.index;
          const currentCalculationIndex = get().currentCalculation.index;

          if (
            currentCountingIndex !== undefined &&
            currentCountingIndex >= 0 &&
            currentCalculationIndex !== undefined &&
            currentCalculationIndex >= 0
          ) {
            const updatedPreparations = currentPreparations.map(
              (preparation, index) => {
                if (
                  preparation.analysis_type.some(
                    (analysis) =>
                      analysis.id === processing.analysis_type &&
                      preparation.id === currentPreparationId
                  )
                ) {
                  const updatedCountings = [
                    ...(preparation.countings as any[]),
                  ];
                  const updatedCalculations = [
                    ...updatedCountings[currentCountingIndex].calculations,
                  ];

                  updatedCalculations[currentCalculationIndex] = response.data;
                  updatedCountings[currentCountingIndex] = {
                    ...updatedCountings[currentCountingIndex],
                    calculations: updatedCalculations,
                  };

                  return {
                    ...preparation,
                    countings: updatedCountings,
                  };
                }

                return preparation;
              }
            );

            set({
              preparationsList: updatedPreparations,
            });
          }

          set({
            currentCalculation: {
              ...get().currentCalculation,
              id: response.data.id,
            },
          });
        }
        successAlert("Elemento guardado correctamente");
        return response.data.id;
      } else {
        errorAlert("Error al guardar el elemento");
        return undefined;
      }
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return undefined;
    }
  },
  saveProcessingWhitMeasure: async (
    processing: ProccesingTypes,
    type: string
  ) => {
    try {
      const isNew = !processing?.id;
      const endpoint = `/${type}/${isNew ? "create" : processing.id + "/edit"}`;

      const response = isNew
        ? await postData(endpoint, processing)
        : await putData(endpoint, processing);
      if (response.success) {
        successAlert("Elemento guardado correctamente");

        if (type === AnalysisType.PREPARATION) {
          const currentPreparations = get().preparationsList;
          const currentIndex = get().currentPreparation.index;

          if (currentIndex !== undefined && currentIndex >= 0) {
            const filteredPreparations = currentPreparations.filter(
              (preparation) =>
                preparation.analysis_type.some(
                  (analysis) => analysis.id === processing.analysis_type
                )
            );

            const updatedFilteredPreparations = filteredPreparations.map(
              (preparation, index) => {
                if (index === currentIndex) {
                  return {
                    ...preparation,
                    ...response.data,
                  };
                }
                return preparation;
              }
            );

            const updatedPreparations = currentPreparations.map(
              (preparation) => {
                if (
                  preparation.analysis_type.some(
                    (analysis) => analysis.id === processing.analysis_type
                  )
                ) {
                  return updatedFilteredPreparations.shift()!;
                }
                return preparation;
              }
            );

            set({
              preparationsList: updatedPreparations.sort((a, b) => a.id - b.id),
            });
          }

          set({
            currentPreparation: {
              ...get().currentPreparation,
              id: response.data.id,
            },
          });
        }

        // Actualización para preparación
        if (type === AnalysisType.MEASUREMENT) {
          const currentPreparations = get().preparationsList;
          const preparationIndex = get().currentPreparation.index;
          const currentMeasurementIndex = get().currentMeasurement.index;

          if (
            preparationIndex !== undefined &&
            preparationIndex >= 0 &&
            currentMeasurementIndex !== undefined &&
            currentMeasurementIndex >= 0
          ) {
            const filteredPreparations = currentPreparations.filter(
              (preparation) =>
                preparation.analysis_type.some(
                  (analysis) => analysis.id === processing.analysis_type
                )
            );

            const updatedFilteredPreparations = filteredPreparations.map(
              (preparation, index) => {
                if (index === preparationIndex) {
                  const updatedMeasurements = [
                    ...(preparation.aa_measures as any[]),
                  ];

                  // Preserve existing countings from the current measurement
                  const existingCountings = updatedMeasurements[currentMeasurementIndex]?.countings || [];
                  
                  updatedMeasurements[currentMeasurementIndex] = {
                    ...response.data,
                    countings: existingCountings,
                  };
                  return {
                    ...preparation,
                    aa_measures: updatedMeasurements,
                  };
                }
                return preparation;
              }
            );

            set({
              preparationsList: updatedFilteredPreparations,
            });
          }

          set({
            currentMeasurement: {
              ...get().currentMeasurement,
              id: response.data.id,
            },
          });
        }
        if (type === AnalysisType.COUNTING) {
          const currentPreparations = get().preparationsList;
          const currentPreparationId = get().currentPreparation.id;
          const measurementIndex = get().currentMeasurement.index as number;
          const currentCountingIndex = get().currentCounting.index as number;

          const updatedPreparations = currentPreparations.map(
            (preparation, index) => {
              if (
                preparation.analysis_type.some(
                  (analysis) =>
                    analysis.id === processing.analysis_type &&
                    preparation.id === currentPreparationId
                )
              ) {
                const updatedMeasurements = [
                  ...(preparation.aa_measures as any[]),
                ];

                const updatedCouting = [
                  ...updatedMeasurements[measurementIndex].countings,
                ];

                updatedCouting[currentCountingIndex] = response.data;
                updatedMeasurements[measurementIndex] = {
                  ...updatedMeasurements[measurementIndex],
                  countings: updatedCouting,
                };

                return {
                  ...preparation,
                  aa_measures: updatedMeasurements,
                };
              }

              return preparation;
            }
          );

          set({
            preparationsList: updatedPreparations,
          });
        }

        set({
          currentCalculation: {
            ...get().currentCalculation,
            id: response.data.id,
          },
        });

        if (type === AnalysisType.CALCULATION) {
          const currentPreparations = get().preparationsList;
          const preparationIndex = get().currentPreparation.index;
          const currentPreparationId = get().currentPreparation.id;
          const currentMeasurementIndex = get().currentMeasurement.index;
          const currentCountingIndex = get().currentCounting.index;
          const currentCalculationIndex = get().currentCalculation.index;

          if (
            preparationIndex !== undefined &&
            preparationIndex >= 0 &&
            currentMeasurementIndex !== undefined &&
            currentMeasurementIndex >= 0 &&
            currentCountingIndex !== undefined &&
            currentCountingIndex >= 0 &&
            currentCalculationIndex !== undefined &&
            currentCalculationIndex >= 0
          ) {
            const updatedPreparations = currentPreparations.map(
              (preparation, index) => {
                if (
                  preparation.analysis_type.some(
                    (analysis) =>
                      analysis.id === processing.analysis_type &&
                      preparation.id === currentPreparationId
                  )
                ) {
                  const updatedMeasurements = [
                    ...(preparation.aa_measures as any[]),
                  ];

                  const updatedCountings = [
                    ...updatedMeasurements[currentMeasurementIndex].countings,
                  ];

                  const updatedCalculations = [
                    ...updatedCountings[currentCountingIndex].calculations,
                  ];

                  updatedCalculations[currentCalculationIndex] = response.data;

                  updatedCountings[currentCountingIndex] = {
                    ...updatedCountings[currentCountingIndex],
                    calculations: updatedCalculations,
                  };

                  updatedMeasurements[currentMeasurementIndex] = {
                    ...updatedMeasurements[currentMeasurementIndex],
                    countings: updatedCountings,
                  };

                  return {
                    ...preparation,
                    aa_measures: updatedMeasurements,
                  };
                }

                return preparation;
              }
            );

            set({
              preparationsList: updatedPreparations,
            });
          }

          set({
            currentCalculation: {
              ...get().currentCalculation,
              id: response.data.id,
            },
          });
        }

        return response.data.id;
      } else {
        errorAlert("Error al guardar el elemento");
        return undefined;
      }
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return undefined;
    }
  },
  saveCalculate: async (codeCurrentAnalysis: string) => {
    set({ loadingResults: true });
    const currentCalculationId = get().currentCalculation.id;
    try {
      const endpoint = "/results/create";
      const payload = {
        calculation: get().currentCalculation.id,
      };
      const response = await postData(endpoint, payload);
      if (!response.success) {
        errorAlert("Error al generar los resultados: " + response.error);
        console.error(response.error);
        return false;
      } else {
        const updatedPreparationsList = get().preparationsList.map(
          (preparation) => {
            let updatedCountings: any = [];

            if (
              !AnalysisWhitMeasurement.includes(
                preparation.analysis_type[0].codigo as KeysTabsAnalysis
              )
            ) {
              updatedCountings = preparation?.countings?.map((counting) => {
                const updatedCalculations = counting.calculations.map(
                  (calculation) => {
                    if (calculation.id === currentCalculationId) {
                      return {
                        ...calculation,
                        result: {
                          id: response.data.id,
                          to_reports: response.data.to_reports,
                        },
                      };
                    } else {
                      return {
                        ...calculation,
                        result: {
                          id: calculation?.result?.id,
                          to_reports:
                            preparation.analysis_type[0].codigo ===
                            codeCurrentAnalysis
                              ? false
                              : calculation?.result?.to_reports,
                        },
                      };
                    }
                  }
                );
                return { ...counting, calculations: updatedCalculations };
              });
              return { ...preparation, countings: updatedCountings };
            } else {
              updatedCountings = preparation?.aa_measures?.map((measure) => {
                const updatedCountings = measure.countings.map((counting) => {
                  const updatedCalculations = counting.calculations.map(
                    (calculation) => {
                      if (calculation.id === currentCalculationId) {
                        return {
                          ...calculation,
                          result: {
                            id: response.data.id,
                            to_reports: response.data.to_reports,
                          },
                        };
                      } else {
                        return {
                          ...calculation,
                          result: {
                            id: calculation?.result?.id,
                            to_reports:
                              preparation.analysis_type[0].codigo ===
                              codeCurrentAnalysis
                                ? false
                                : calculation?.result?.to_reports,
                          },
                        };
                      }
                    }
                  );
                  return { ...counting, calculations: updatedCalculations };
                });
                return { ...measure, countings: updatedCountings };
              });
              return { ...preparation, aa_measures: updatedCountings };
            }
          }
        );

        set({ preparationsList: updatedPreparationsList });
        return response.data;
      }
    } catch (error) {
      console.error(error);

      errorAlert("Error al guardar los resultados");
      return false;
    } finally {
      set({ loadingResults: false });
    }
  },
  saveResultCalculate: async (
    codeCurrentAnalysis: string,
    result: ResultsTypes
  ) => {
    set({ loadingResults: true });
    const currentCalculationId = get().currentCalculation.id;
    try {
      const endpoint = "/results/create";
      const payload = {
        calculation: get().currentCalculation.id,
        ...result,
      };
      const response = await postData(endpoint, payload);
      if (!response.success) {
        errorAlert("Error al generar los resultados: " + response.error);
        console.error(response.error);
        return false;
      } else {
        const updatedPreparationsList = get().preparationsList.map(
          (preparation) => {
            let updatedCountings: any = [];

            if (
              !AnalysisWhitMeasurement.includes(
                preparation.analysis_type[0].codigo as KeysTabsAnalysis
              )
            ) {
              updatedCountings = preparation?.countings?.map((counting) => {
                const updatedCalculations = counting.calculations.map(
                  (calculation) => {
                    if (calculation.id === currentCalculationId) {
                      return {
                        ...calculation,
                        result: {
                          id: response.data.id,
                          to_reports: response.data.to_reports,
                        },
                      };
                    } else {
                      return {
                        ...calculation,
                        result: {
                          id: calculation?.result?.id,
                          to_reports:
                            preparation.analysis_type[0].codigo ===
                            codeCurrentAnalysis
                              ? false
                              : calculation?.result?.to_reports,
                        },
                      };
                    }
                  }
                );
                return { ...counting, calculations: updatedCalculations };
              });
              return { ...preparation, countings: updatedCountings };
            } else {
              updatedCountings = preparation?.aa_measures?.map((measure) => {
                const updatedCountings = measure.countings.map((counting) => {
                  const updatedCalculations = counting.calculations.map(
                    (calculation) => {
                      if (calculation.id === currentCalculationId) {
                        return {
                          ...calculation,
                          result: {
                            id: response.data.id,
                            to_reports: response.data.to_reports,
                          },
                        };
                      } else {
                        return {
                          ...calculation,
                          result: {
                            id: calculation?.result?.id,
                            to_reports:
                              preparation.analysis_type[0].codigo ===
                              codeCurrentAnalysis
                                ? false
                                : calculation?.result?.to_reports,
                          },
                        };
                      }
                    }
                  );
                  return { ...counting, calculations: updatedCalculations };
                });
                return { ...measure, countings: updatedCountings };
              });
              return { ...preparation, aa_measures: updatedCountings };
            }
          }
        );

        set({ preparationsList: updatedPreparationsList });
        return response.data;
      }
    } catch (error) {
      console.error(error);

      errorAlert("Error al guardar los resultados");
      return false;
    } finally {
      set({ loadingResults: false });
    }
  },
  fetchCalculation: async (id: number) => {
    set({ loadingResults: true });
    try {
      const endpoint = `/results/${id}`;
      const response = await getData(endpoint);
      if (response.success) {
        return response.data;
      } else {
        errorAlert("Error al obtener los datos del cálculo");
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      set({ loadingResults: false });
    }
  },

  remove: async (
    idTypeAnalysis: number,
    type: string,
    id?: number,
    idxPreparation?: number,
    idxCounting?: number,
    idxCalculation?: number
  ) => {
    const { preparationsList } = get();
    let updatedPreparations = preparationsList.filter((preparation) =>
      preparation.analysis_type.some(
        (analysis) => analysis.id === idTypeAnalysis
      )
    );

    if (id === undefined) {
      if (type === AnalysisType.PREPARATION) {
        updatedPreparations = removePreparationByIndex(
          updatedPreparations,
          idxPreparation!
        );
      } else if (type === AnalysisType.COUNTING) {
        updatedPreparations = removeCountingByIndex(
          updatedPreparations,
          idxPreparation!,
          idxCounting!
        );
      } else if (type === AnalysisType.CALCULATION) {
        updatedPreparations = removeCalculationByIndex(
          updatedPreparations,
          idxPreparation!,
          idxCounting!,
          idxCalculation!
        );
      }

      const finalPreparations = updatePreparations(
        preparationsList,
        updatedPreparations,
        idTypeAnalysis
      );
      successAlert("Elemento eliminado correctamente");
      set({ preparationsList: finalPreparations });
      setTimeout(() => {
        set({
          currentPreparation: { id: undefined, index: undefined },
          currentCounting: { id: undefined, index: undefined },
          currentCalculation: {
            id: undefined,
            index: undefined,
            result: undefined,
          },
        });
      }, 500);
    } else {
      try {
        const endpoint = `/${type}/${id}/edit`;
        const response = await putData(endpoint, { state: 0 });
        if (response.success) {
          if (type === AnalysisType.PREPARATION) {
            updatedPreparations = removePreparationByIndex(
              updatedPreparations,
              idxPreparation!
            );
          } else if (type === AnalysisType.COUNTING) {
            updatedPreparations = removeCountingByIndex(
              updatedPreparations,
              idxPreparation!,
              idxCounting!
            );
          } else if (type === AnalysisType.CALCULATION) {
            updatedPreparations = removeCalculationByIndex(
              updatedPreparations,
              idxPreparation!,
              idxCounting!,
              idxCalculation!
            );
          }

          const finalPreparations = updatePreparations(
            preparationsList,
            updatedPreparations,
            idTypeAnalysis
          );
          successAlert("Elemento eliminado correctamente");
          set({ preparationsList: finalPreparations });
          set({
            currentPreparation: { id: undefined, index: undefined },
            currentCounting: { id: undefined, index: undefined },
            currentCalculation: {
              id: undefined,
              index: undefined,
              result: undefined,
            },
          });
        } else {
          errorAlert("Error al eliminar el elemento");
        }
      } catch (error) {
        errorAlert("Error al eliminar el elemento");
      }
    }
  },

  removeWithMeasure: async (
    idTypeAnalysis: number,
    type: string,
    id?: number,
    idxPreparation?: number,
    idxMeasurement?: number,
    idxCounting?: number,
    idxCalculation?: number
  ) => {
    const { preparationsList } = get();
    let updatedPreparations = preparationsList.filter((preparation) =>
      preparation.analysis_type.some(
        (analysis) => analysis.id === idTypeAnalysis
      )
    );

    if (id === undefined) {
      if (type === AnalysisType.PREPARATION) {
        updatedPreparations = removePreparationByIndex(
          updatedPreparations,
          idxPreparation!
        );
      } else if (type === AnalysisType.MEASUREMENT) {
        updatedPreparations = removeMeasurementByIndex(
          updatedPreparations,
          idxPreparation!,
          idxMeasurement!
        );
      } else if (type === AnalysisType.COUNTING) {
        updatedPreparations = removeCountingByIndexWithMeasurement(
          updatedPreparations,
          idxPreparation!,
          idxMeasurement!,
          idxCounting!
        );
      } else if (type === AnalysisType.CALCULATION) {
        updatedPreparations = removeCalculationByIndexWithMeasurement(
          updatedPreparations,
          idxPreparation!,
          idxMeasurement!,
          idxCounting!,
          idxCalculation!
        );
      }

      const finalPreparations = updatePreparations(
        preparationsList,
        updatedPreparations,
        idTypeAnalysis
      );
      successAlert("Elemento eliminado correctamente");
      set({ preparationsList: finalPreparations });
    } else {
      try {
        const endpoint = `/${type}/${id}/edit`;
        const response = await putData(endpoint, { state: 0 });
        if (response.success) {
          if (type === AnalysisType.PREPARATION) {
            updatedPreparations = removePreparationByIndex(
              updatedPreparations,
              idxPreparation!
            );
          } else if (type === AnalysisType.MEASUREMENT) {
            updatedPreparations = removeMeasurementByIndex(
              updatedPreparations,
              idxPreparation!,
              idxMeasurement!
            );
          } else if (type === AnalysisType.COUNTING) {
            updatedPreparations = removeCountingByIndexWithMeasurement(
              updatedPreparations,
              idxPreparation!,
              idxMeasurement!,
              idxCounting!
            );
          } else if (type === AnalysisType.CALCULATION) {
            updatedPreparations = removeCalculationByIndexWithMeasurement(
              updatedPreparations,
              idxPreparation!,
              idxMeasurement!,
              idxCounting!,
              idxCalculation!
            );
          }

          const finalPreparations = updatePreparations(
            preparationsList,
            updatedPreparations,
            idTypeAnalysis
          );
          successAlert("Elemento eliminado correctamente");
          set({ preparationsList: finalPreparations });

          setTimeout(() => {
            set({
              currentPreparation: { id: undefined, index: undefined },
              currentMeasurement: { id: undefined, index: undefined },
              currentCounting: { id: undefined, index: undefined },
              currentCalculation: {
                id: undefined,
                index: undefined,
                result: undefined,
              },
            });
          }, 500);
        } else {
          errorAlert("Error al eliminar el elemento");
        }
      } catch (error) {
        errorAlert("Error al eliminar el elemento");
      }
    }
  },

  putResult: async (idResult: number, toReport: boolean) => {
    try {
      const endpoint = `/results/${idResult}/edit`;
      const response = await putData(endpoint, {
        to_reports: toReport ? 1 : 0,
      });
      if (response.success) {
        if (toReport) {
          const { preparationsList } = get();
          let idTypeAnalysis;
          preparationsList.forEach((preparation) => {
            idTypeAnalysis = preparation.analysis_type[0].id;
            preparation.countings?.forEach((counting) => {
              counting.calculations?.forEach((calculation) => {
                if (calculation.result) {
                  if (calculation.result.id == idResult) {
                    calculation.result.to_reports = true;
                  } else calculation.result.to_reports = false;
                }
              });
            });
          });
          updatePreparations(
            preparationsList,
            preparationsList,
            idTypeAnalysis
          );
          set({ preparationsList: preparationsList });
        }

        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  },
  saveFileITIG: async (file, name) => {
    try {
      const endpoint = `/calculation/file`;
      const response = await postDataFileDat(endpoint, {
        [name]: file,
        id: get().currentCalculation.id,
      });
      if (!response.success) {
        errorAlert("Error al guardar el archivo");
        return false;
      }
      return true;
    } catch (error) {
      errorAlert("Error al guardar el archivo");
      return false;
    }
  },
  updateCoutingITIG: async (processing: ProccesingTypes) => {
    const endpoint = `/counting/${get().currentCounting.id}/edit`;
    const response = await putData(endpoint, {
      ...processing,
      id: get().currentCounting.id,
    });

    if (response.success) {
      const currentPreparations = get().preparationsList;
      const preparationIndex = get().currentPreparation.index;
      const currentCountingIndex = get().currentCounting.index;
      const currentPreparationId = get().currentPreparation.id;

      if (
        preparationIndex !== undefined &&
        preparationIndex >= 0 &&
        currentCountingIndex !== undefined &&
        currentCountingIndex >= 0
      ) {
        const updatedFilteredPreparations = currentPreparations.map(
          (preparation, index) => {
            if (
              preparation.analysis_type.some(
                (analysis) =>
                  analysis.id === processing.analysis_type &&
                  preparation.id === currentPreparationId
              )
            ) {
              const updatedCountings = [...(preparation.countings as any[])];
              const memoCalculation =
                updatedCountings[currentCountingIndex].calculations;

              updatedCountings[currentCountingIndex] = {
                ...response.data,
                calculations: memoCalculation,
              };
              return {
                ...preparation,
                countings: updatedCountings,
              };
            }
            return preparation;
          }
        );

        set({
          preparationsList: updatedFilteredPreparations,
        });
      }

      set({
        currentCounting: {
          ...get().currentCounting,
          id: response.data.id,
        },
      });
    }
  },
  importFileFromRoute: async (route, action) => {
    set({ loadingFile: true });
    try {
      const fullUrl = `/calculation/file/${route}`;

      const { data: response } = await getDataFile(fullUrl);

      if (action === "DOWNLOAD") {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${route}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    } finally {
      set({ loadingFile: false });
    }
  },
  importFileCalculation: async (nameFile, action) => {
    set({ loadingFile: true });
    try {
      const endpoint = `/calculation/file/${nameFile}`;
      const { data: response } = await getDataFile(endpoint, {
        responseType: "blob",
      });

      // Convertir el Blob a texto
      const responseText = await response.text();

      try {
        const jsonResponse = JSON.parse(responseText);
        if (jsonResponse.message) {
          errorAlert(jsonResponse.message);
          return undefined;
        }
      } catch (e) {}

      if (action === "DOWNLOAD") {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", nameFile);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        successAlert("Archivo descargado correctamente");

        return undefined;
      } else {
        if (typeof responseText !== "string") {
          throw new Error("responseText no es un string válido");
        }

        return responseText; // Devuelve un array de líneas
      }
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    } finally {
      set({ loadingFile: false });
    }
  },
  updateCalculationById: async () => {
    try {
      const currentCalculationID = get().currentCalculation.id;

      const { data: responseCalculation } = await getData(
        `/calculation/${currentCalculationID}`
      );

      const currentPreparations = get().preparationsList;
      const currentPreparationId = get().currentPreparation.id;
      const currentCountingIndex = get().currentCounting.index;
      const currentCalculationIndex = get().currentCalculation.index;

      if (
        currentCountingIndex !== undefined &&
        currentCountingIndex >= 0 &&
        currentCalculationIndex !== undefined &&
        currentCalculationIndex >= 0
      ) {
        const updatedPreparations = currentPreparations.map(
          (preparation, index) => {
            if (preparation.id === currentPreparationId) {
              const updatedCountings = [...(preparation.countings as any[])];
              const updatedCalculations = [
                ...updatedCountings[currentCountingIndex].calculations,
              ];

              updatedCalculations[currentCalculationIndex] =
                responseCalculation;
              updatedCountings[currentCountingIndex] = {
                ...updatedCountings[currentCountingIndex],
                calculations: updatedCalculations,
              };

              return {
                ...preparation,
                countings: updatedCountings,
              };
            }

            return preparation;
          }
        );

        set({
          preparationsList: updatedPreparations,
        });
      }

      set({
        currentCalculation: {
          ...get().currentCalculation,
          id: responseCalculation.id,
        },
      });

      return true;
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return false;
    }
  },
  saveIsotopeResultCalculation: async (
    isotopeResult: IsotopeResultCalculationPOSTTypes[]
  ) => {
    try {
      const endpoint = `/generic/IsotopeResultCalculation`;
      const response = await postData(endpoint, isotopeResult);
      if (response.success) {
        return true;
      } else {
        errorAlert("Error al guardar el elemento");
        return false;
      }
    } catch (error) {
      errorAlert("Error al guardar el elemento");
      return false;
    }
  },
  fetchIsotopeResultCalculationByID: async () => {
    set({ loadingIsotopeResultCalculation: true });
    const currentCalculation = get().currentCalculation.id;
    const url = `/generic/IsotopeResultCalculation/filtered?calculation=${currentCalculation}`;
    try {
      const { data: responseIsotopeResultCalculation } = await getData(url);

      set({
        currentIsotopeResultCalculationResponseTypes:
          responseIsotopeResultCalculation.length > 0
            ? responseIsotopeResultCalculation
            : [],
      });
      return responseIsotopeResultCalculation;
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    } finally {
      set({ loadingIsotopeResultCalculation: true });
    }
  },
  deleteIsotopeResultCalculationByID: async (id: number) => {
    try {
      const endpoint = `/generic/IsotopeResultCalculation/${id}`;
      await deleteData(endpoint);
    } catch (error) {
      console.error("Error:", error);
    }
  },
  updateRequestedByClient: async (id: number, value: number) => {
    try {
      const endpoint = `/generic/IsotopeResultCalculation/${id}`;
      await putData(endpoint, { requested_by_client: value });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      get().fetchIsotopeResultCalculationByID();
    }
  },
  saveIsotopeResultAxe: async (isotopeResult: any[]) => {
    try {
      const currentCalculation = get().currentCalculation.id;
      const endpoint = `/generic/IsotopeResultCalculation`;
      
      // Verificar si ya existen registros para este cálculo
      const url = `/generic/IsotopeResultCalculation/filtered?calculation=${currentCalculation}`;
      const { data: existingIsotopes } = await getData(url);
      
      // Si ya existen registros, no hacer nada
      if (existingIsotopes && existingIsotopes.length > 0) {
        return true;
      }
      
      // Solo si NO existen registros, obtener el catálogo de isótopos y guardar
      const endpointIsotope = "/generic/Isotopos";
      const { data: isotopes } = await getData(endpointIsotope);
      
      // Guardar cada isótopo individualmente, como lo hace Genie
      for (const isotope of isotopeResult) {
        // Buscar el isótopo en el catálogo por código
        const isotopeMatch = isotopes.find(
          (iso) => iso.codigo.toUpperCase() === isotope.isotopoFile.toUpperCase()
        );
        
        const payload = {
          calculation: currentCalculation,
          isotope: isotopeMatch ? isotopeMatch.id : null,
          activity: isotope.actividad,
          uncertainty: isotope.incertidumbre,
          lid: isotope.lid,
          lc: 0, // LC no se calcula en Area Exe
          requested_by_client: isotope.requested_by_client ? 1 : 0,
        };
        
        const response = await postData(endpoint, payload);
        if (!response.success) {
          console.error("Error al guardar isótopo:", isotope);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error("Error:", error);
      return false;
    }
  },
  fetchIsotopeResultAxeByID: async () => {
    set({ loadingIsotopeResultCalculation: true });
    const currentCalculation = get().currentCalculation.id;
    const url = `/generic/IsotopeResultCalculation/filtered?calculation=${currentCalculation}`;
    try {
      const { data: responseIsotopeResult } = await getData(url);
      
      // Filtrar solo los que vienen de Area Exe (tienen isotopo_file pero no isotope)
      const axeResults = responseIsotopeResult.filter(item => item.isotopo_file && !item.isotope);
      
      return axeResults.length > 0
        ? axeResults.map((item) => ({
            isotopoFile: item.isotopo_file,
            energy: item.energy,
            cuenta: item.cuenta,
            efficiency: item.efficiency,
            error: item.error,
            actividad: item.activity,
            incertidumbre: item.uncertainty,
            lid: item.lid,
            fondo_integration_to_apply: item.fondo_integration_to_apply,
            fondo_area_to_apply: item.fondo_area_to_apply,
            fondo_area_error_to_apply: item.fondo_area_error_to_apply,
            requested_by_client: item.requested_by_client === 1,
            id: item.id,
          }))
        : [];
    } catch (error) {
      console.error("Error:", error);
      return [];
    } finally {
      set({ loadingIsotopeResultCalculation: false });
    }
  },
  calculateDataOutIGAxe: async (
    routeFile1,
    routeFile2,
    equipmentCounting,
    dateCounting,
    calculation_self_absorption_factor,
    performance,
    sample_amount,
    calculation_geometric_factor,
    diffDate,
    cod_instalacion,
    ref_cliente
  ) => {
    try {
      set({ loadingDateAxe: true });
      const ID_CODE_IG = 29;
      const responseFile2 = await get().importFileFromRoute(
        routeFile2,
        "TO_FORM"
      );
      if (!responseFile2) return [];
      const responseFile1 = await get().importFileFromRoute(
        routeFile1,
        "TO_FORM"
      );
      if (!responseFile1) return [];

      const lines: string[][] = parseRptFileExe(responseFile1);
      const searchKey = `${cod_instalacion}${ref_cliente}`;
      const matchingLine = lines.find(line => line[0] === searchKey);
      
      if (!matchingLine) return [];
      
      const counting_time = matchingLine[1] || 0;
      const count_351 = matchingLine[4];
      const count_911 = matchingLine[5];

      const endpointIsotope = "/generic/Isotopos";
      const { data: isotopes } = await getData(endpointIsotope);

      var endpointEfficiencies = `/generic/Efficiencies/filtered?analysis=${ID_CODE_IG}`;
      if(equipmentCounting.length == 2){
        let idEquipo = equipmentCounting[0].id;
        let idGeometria = equipmentCounting[1].id;
        endpointEfficiencies = endpointEfficiencies + `&equipment=${idEquipo}`;
      }
      
      const { data: efficiencies } = await getData(endpointEfficiencies);

      const endpointFondos = "/fondos";
      const { data: fondosList } = await getData(endpointFondos);

      const filterFondos = fondosList.filter(
        (fondo) => fondo.analysis_type?.id === ID_CODE_IG
      );

      const filterEfficiencies = efficiencies.filter((efficiency) => {
        const efficiencyDate = new Date(efficiency.date).getTime();
        const countingDate = new Date(dateCounting + "Z").getTime();
        const code_geometry = efficiency.cs_source_geometry ? efficiency.cs_source_geometry.codigo.replace(/[^A-Za-z]+/g, "") : ''; 
        
        const geometryEquipment = equipmentCounting.find(eq => eq.is_geometry === true);
          return efficiencyDate <= countingDate && 
              geometryEquipment && 
              (code_geometry == geometryEquipment.codigo.replace(/[^A-Za-z]+/g, ""));
      });

      if (filterEfficiencies && filterEfficiencies.length > 0) {
        for (let i = 0; i < efficiencies.length; i++) {
          const efficienciesIsotopes = await useEfficiencies
            .getState()
            .fechtEfficienciesIsotopes(efficiencies[i].id);
          efficiencies[i].efficienciesIsotopes = efficienciesIsotopes;
        }
      }

      const geometry = equipmentCounting.find(eq => eq.is_geometry === true);
      const equipment = equipmentCounting.find(eq => eq.is_geometry === false);

      const parsedData = parseIsotopeData(
        responseFile2,
        isotopes,
        efficiencies,
        filterFondos,
        counting_time,
        count_351,
        count_911,
        calculation_self_absorption_factor,
        calculation_geometric_factor,
        sample_amount,
        diffDate,
        performance,
        cod_instalacion,
        ref_cliente,
        geometry,
        equipment,
        dateCounting
      );
      
      return parsedData;
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    } finally {
      set({ loadingDateAxe: false });
    }
  },
  calculateDataOutGT: async (
    equipmentCounting,
    cpsSpectrumArea,
    absorptionFactor,
    geometricFactor,
    performance,
    amountSample,
    integralSpectrum,
    countingTime
  ) => {
    try {
      set({ loadingDateAxe: true });
      const ID_CODE_GT = 30;
      const endpointFondos = "/fondos";
      const { data: fondosList } = await getData(endpointFondos);
      const endpointIsotope = "/generic/isotopos/filtered?codigo=CS137";
      const { data: isotopes } = await getData(endpointIsotope);
      const endpointEfficiencies = `/generic/Efficiencies/filtered?analysis=${ID_CODE_GT}&equipment=${equipmentCounting}`;

      const cpsSpectrumAreaNumber = Number(cpsSpectrumArea) || 0;
      const absorptionFactorNumber = Number(absorptionFactor) || 0;
      const geometricFactorNumber = Number(geometricFactor) || 0;
      const performanceNumber = Number(performance) || 0;
      const amountSampleNumber = Number(amountSample) || 0;
      const integralSpectrumNumber = Number(integralSpectrum) || 0;
      const countingTimeNumber = Number(countingTime) || 0;

      const { data: efficiencies } = await getData(endpointEfficiencies);

      const filterFondos = fondosList
        .sort((a, b) => new Date(a.calculation_date).getTime() - new Date(b.calculation_date).getTime())
        .find(
        (fondo) => fondo.analysis_type?.id === ID_CODE_GT &&
        fondo.equipment?.id == equipmentCounting
      );

      const lastEfficiency =
        efficiencies && efficiencies.length > 0
          ? efficiencies[efficiencies.length - 1]
          : undefined;
      const avgIntegralFondo = filterFondos
        ? filterFondos.avg_integral_fondo
        : 0;
      const cdes157 = isotopes[0].gammades;
      const gammades157 = isotopes[0].ugammades;
      const cs_source_pattern_efficiency =
        lastEfficiency?.cs_source_pattern_efficiency || 0;

      const incertidumbreNominador =
        2 *
        Math.sqrt(
          integralSpectrumNumber + avgIntegralFondo * countingTimeNumber
        );

      const incertidumbreDenominador =
        countingTimeNumber *
        (absorptionFactorNumber / geometricFactorNumber) *
        performanceNumber *
        cs_source_pattern_efficiency *
        amountSampleNumber;

      const lidNominador =
        4.66 *
        Math.sqrt(
          avgIntegralFondo * countingTimeNumber
        );
      const lidDenominador =
        countingTimeNumber *
        (absorptionFactorNumber / geometricFactorNumber) *
        performanceNumber *
        cs_source_pattern_efficiency *
        amountSampleNumber;

      const lid = lidNominador / lidDenominador;
      const incertidumbre = incertidumbreNominador / incertidumbreDenominador;
      const activity =
        cpsSpectrumAreaNumber /
        (cs_source_pattern_efficiency *
          (absorptionFactorNumber / geometricFactorNumber) *
          performanceNumber *
          amountSampleNumber);

      return {
        result: [
          {
            label: "ACTIVIDAD GAMMA TOTAL",
            value: !isNaN(activity) ? activity : 0,
          },
          {
            label: "INCERTIDUMBRE (K=2)",
            value: !isNaN(incertidumbre) ? incertidumbre : 0,
          },
          { label: "LID", value: !isNaN(lid) ? lid : 0 },
        ],
        outData: [
          {
            label: "Eficiencia GT (Cs137)",
            value: cs_source_pattern_efficiency,
          },
          {
            label: "Cte. Desintegración λ(min-1) Cs-137",
            value: toExponentialUppercase(Number(cdes157), 2),
          },
          { label: "gpd (Cs-137)", value: gammades157 },
          {
            label: "Fondo integral medio (cps)",
            value: toExponentialUppercase(Number(avgIntegralFondo), 2),
          },
        ],
      };
    } catch (error) {
      console.error("Error:", error);
      return undefined;
    } finally {
      set({ loadingDateAxe: false });
    }
  },
}));

export default useAnalysis;
